import React, { ReactElement, useEffect, useRef, useState } from 'react';
import { colors, theme } from '../../theme';
import { CUCombinationType, CUMasterDataType } from '../../types/cu.types';
import { makeStyles } from '@mui/styles';
import {
	Pagination,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TablePagination,
	TableRow,
} from '@mui/material';
import { CustomAlert } from '../shared/alert/alert';
import { CustomButton } from '../shared/button/button';
import { Loader } from '../shared/loader/loader';
import { MultiSelectDropDown } from '../shared/dropdown/multiSelect';
import { LabelGrade, ProductStatus } from '../../config/cuTabConfig';
import { getMaterialCombination } from '../../services/cu-service';
import { useAsyncOperation } from '../../utils/use-async-operation';
import { ToolTip } from '../shared/tooltip/tooltip';
import SearchIcon from '@mui/icons-material/Search';
import DeleteIcon from '@mui/icons-material/Delete';

export const testid = 'created-products';

const color = colors();
const themes = theme().typography;
export const CreatedProducts = ({ master }: CUMasterDataType): ReactElement => {
	const classes = useStyles();
	const rawMaterialList = master.rawMaterial.map((item) => ({
		...item,
		name: item.name.replace('__', ' - '),
	}));
	const productCategoryList = master.productCategory.map((item) => ({
		...item,
		name: item.name.replace('__', ' - '),
	}));
	const productDetailList = master.productDetail.map((item) => ({
		...item,
		name: item.name.replace('__', ' - '),
	}));

	const [initialData, setInitialData] = useState<CUCombinationType[]>([]);
	const [data, setData] = useState<CUCombinationType[]>([]);
	const [page, setPage] = useState(1);
	const [rowsPerPage, setRowsPerPage] = useState<number>(25);
	const [pageCount, setPageCount] = useState<number>(1);
	const [errorAlertOpen, setErrorAlertOpen] = useState(false);
	const [productCategory, setProductCategory] = useState<string[]>([]);
	const [productDetail, setProductDetail] = useState<string[]>([]);
	const [rawMaterial, setRawMaterial] = useState<string[]>([]);
	const [labelGrade, setLabelGrade] = useState<string[]>([]);
	const [status, setStatus] = useState<string[]>([]);
	const initialLoadTriggered = useRef(false);

	const [getCombinations, dataBusy, materialCombination, dataError] =
		useAsyncOperation<CUCombinationType[]>(getMaterialCombination);

	useEffect(() => {
		if (initialLoadTriggered.current) return;
		initialLoadTriggered.current = true;
		getCombinations();
	}, []); // eslint-disable-line react-hooks/exhaustive-deps

	useEffect(() => {
		if (dataError) setErrorAlertOpen(true);
	}, [dataError]);

	useEffect(() => {
		if (materialCombination) {
			const sortedData: CUCombinationType[] = [...materialCombination].sort(
				(a, b) => new Date(b.createdDate).getTime() - new Date(a.createdDate).getTime()
			);
			setInitialData(sortedData);
			setData(sortedData);
			setPageCount(Math.floor(sortedData.length / rowsPerPage));
		}
	}, [materialCombination]); // eslint-disable-line react-hooks/exhaustive-deps

	useEffect(() => {
		if (data) setPageCount(Math.floor(data.length / rowsPerPage));
	}, [rowsPerPage]); // eslint-disable-line react-hooks/exhaustive-deps

	const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
		setPage(value);
	};

	const handleRowPerPageChange = (value: string) => {
		setRowsPerPage(parseInt(value));
		setPage(1);
	};

	const onClearClick = () => {
		setPage(1);
		if (typeof window !== 'undefined' && typeof window.scrollTo === 'function') {
			try {
				window.scrollTo({ top: 0, behavior: 'smooth' });
			} catch {
				window.scrollTo(0, 0);
			}
		}
		setProductCategory([]);
		setProductDetail([]);
		setRawMaterial([]);
		setLabelGrade([]);
		setStatus([]);
		setData(initialData || []);
		setPageCount(Math.floor(initialData.length / rowsPerPage));
	};

	const onSearchClick = () => {
		if (
			productCategory.length !== 0 ||
			productDetail.length !== 0 ||
			rawMaterial.length !== 0 ||
			labelGrade.length !== 0 ||
			status.length !== 0
		) {
			let filteredData = initialData || [];
			setPage(1);

			if (productCategory.length > 0) {
				var pc = productCategory.map((cat) => cat.substring(0, cat.indexOf(' - ')));
				filteredData = filteredData.filter((d) => pc.some((p) => d.productCategory.startsWith(p)));
			}
			if (productDetail.length > 0 && filteredData.length > 0) {
				var pd = productDetail.map((det) => det.substring(0, det.indexOf(' - ')));
				filteredData = filteredData.filter((d) => pd.some((p) => d.productDetail.startsWith(p)));
			}
			if (rawMaterial.length > 0 && filteredData.length > 0) {
				var rm = rawMaterial.map((mat) => mat.substring(0, mat.indexOf(' - ')));
				filteredData = filteredData.filter((d) =>
					d.material
						.split('|')
						.map((m) => m.trim())
						.some((mat) => rm.some((code) => mat.startsWith(code)))
				);
			}
			if (labelGrade.length > 0 && filteredData.length > 0) {
				filteredData = filteredData.filter((d) => labelGrade.some((lg) => d.labelGrade?.includes(lg)));
			}
			if (status.length === 1 && filteredData.length > 0) {
				filteredData = filteredData.filter((d) =>
					status[0] === 'Failed' ? d.cuId?.startsWith('Error:') : !d.cuId?.startsWith('Error:')
				);
			}

			setData(filteredData);
			setPageCount(Math.floor(filteredData.length / rowsPerPage));
		}
	};

	return (
		<>
			<Loader key={`${testid}-loader`} open={dataBusy} message="Loading..." />
			{errorAlertOpen && (
				<CustomAlert
					open={errorAlertOpen}
					message="Something went wrong while retrieving material combination data!"
					severity="error"
					timeout={5000}
					isFade={false}
					onCloseCallback={() => {
						setErrorAlertOpen(false);
					}}
				/>
			)}
			<div className={classes.filters}>
				<MultiSelectDropDown
					label="Product Category"
					width={200}
					data={productCategoryList.map((list) => list.name)}
					value={productCategory}
					hasSearch
					onSelectCallback={setProductCategory}
				/>
				<MultiSelectDropDown
					label="Product Detail"
					width={200}
					data={productDetailList.map((list) => list.name)}
					value={productDetail}
					hasSearch
					onSelectCallback={setProductDetail}
				/>
				<MultiSelectDropDown
					label="Raw Material"
					width={200}
					data={rawMaterialList.map((list) => list.name)}
					value={rawMaterial}
					hasSearch
					onSelectCallback={setRawMaterial}
				/>
				<MultiSelectDropDown
					label="Label Grade"
					width={200}
					data={LabelGrade.map((list) => list.value?.toString() || '')}
					value={labelGrade}
					onSelectCallback={setLabelGrade}
				/>
				<MultiSelectDropDown
					label="Product Status"
					width={200}
					data={ProductStatus.map((list) => list.value?.toString() || '')}
					value={status}
					onSelectCallback={setStatus}
				/>
				<CustomButton
					id="search"
					text=""
					startIcon={<SearchIcon />}
					width={50}
					variant="outlined"
					onClickCallback={onSearchClick}
				/>
				{(productCategory.length > 0 ||
					productDetail.length > 0 ||
					rawMaterial.length > 0 ||
					labelGrade.length > 0 ||
					status.length > 0) && (
					<CustomButton id="clear" text="" startIcon={<DeleteIcon />} width={50} onClickCallback={onClearClick} />
				)}
			</div>

			{data && (
				<>
					<TableContainer className={classes.table} data-testid={`${testid}-table`}>
						<Table stickyHeader>
							<TableHead>
								<TableRow key={`${testid}-header`}>
									<TableCell width={'10%'}>TE Product Category</TableCell>
									<TableCell width={'10%'}>Department</TableCell>
									<TableCell width={'10%'}>TE Product Detail</TableCell>
									<TableCell width={'5%'}>Product Type</TableCell>
									<TableCell width={'15%'}>Raw Material</TableCell>
									<TableCell width={'8%'}>Label Grade</TableCell>
									<TableCell width={'10%'}>Created Date</TableCell>
									<TableCell width={'10%'}>Status</TableCell>
									<TableCell width={'10%'}>CU Validation Status</TableCell>
									<TableCell width={'20%'}>CU Validation Message</TableCell>
								</TableRow>
							</TableHead>
							<TableBody data-testid={`${testid}-body`}>
								{(rowsPerPage > 0
									? data.slice((page - 1) * rowsPerPage, (page - 1) * rowsPerPage + rowsPerPage)
									: data
								).map((row, i) => (
									<TableRow key={i}>
										<TableCell>
											{productCategoryList
												.filter((pc) => pc.name.split(' - ')[0] === row.productCategory.split('__')[0])[0]
												?.name.replace('__', ' - ')}
										</TableCell>
										<TableCell>{row.productCategory.split('__')[1]}</TableCell>
										<TableCell>
											{productDetailList
												.filter((pd) => pd.name.split(' - ')[0] === row.productDetail.split('__')[0])[0]
												?.name.replace('__', ' - ')}
										</TableCell>
										<TableCell>{row.productDetail.split('__')[1]}</TableCell>
										<TableCell>
											{row.material.split('|').map((m, idx) => (
												<React.Fragment key={`m-${i}-${idx}`}>
													{m.replace('__', ' - ')}
													<br />
												</React.Fragment>
											))}
										</TableCell>
										<TableCell>
											{row.labelGrade?.startsWith('GOTS')
												? `${row.labelGrade?.split('__')[0]} (${row.labelGrade?.split('__')[1]})`
												: row.labelGrade}
										</TableCell>
										<TableCell>{new Date(row.createdDate).toLocaleDateString()}</TableCell>
										<TableCell>{row.cuId?.startsWith('Error:') ? row.cuId : 'Success'}</TableCell>
										<TableCell>{!row.cuValidationStatus ? 'Valid' : row.cuValidationStatus}</TableCell>
										<TableCell>
											<ToolTip tooltipText={!row.cuValidationMessage ? 'N/A' : row.cuValidationMessage}>
												<span>
													{!row.cuValidationMessage
														? 'N/A'
														: row.cuValidationMessage.length <= 50
														? row.cuValidationMessage
														: `${row.cuValidationMessage.substring(0, 50)}...`}
												</span>
											</ToolTip>
										</TableCell>
									</TableRow>
								))}
							</TableBody>
						</Table>
					</TableContainer>
					<TablePagination
						data-testid={`${testid}-pagination`}
						component="div"
						className={classes.pagination}
						rowsPerPageOptions={[25, 50, 100]}
						rowsPerPage={rowsPerPage}
						count={data.length}
						page={page - 1}
						onPageChange={() => {}}
						onRowsPerPageChange={(e) => handleRowPerPageChange(e.target.value)}
						ActionsComponent={() => (
							<Pagination
								data-testid={`${testid}-node`}
								count={data.length % rowsPerPage === 0 ? pageCount : pageCount + 1}
								page={page}
								onChange={handlePageChange}
								shape="rounded"
							/>
						)}
					/>
				</>
			)}
		</>
	);
};

const useStyles = makeStyles(() => ({
	filters: {
		paddingTop: '10px',
		display: 'flex',
		alignItems: 'center',
		flexWrap: 'wrap',
		'& #search': {
			backgroundColor: color.primary[100],
			color: color.text.light,
			minWidth: '40px !important',
			width: '40px',
			paddingRight: '5px',
			paddingBottom: '2px',
		},
		'& #clear': {
			minWidth: '40px !important',
			width: '40px',
			paddingRight: '5px',
			paddingBottom: '2px',
		},
		'& #certs': {
			position: 'absolute',
			right: '20px',
			color: color.signal.info,
			textTransform: 'none',
			marginTop: '10px',
		},
	},
	table: {
		height: 'calc(100% - 210px)',
		'& .MuiTableCell-head': {
			...themes.bodyMedium,
			color: color.neutral[8],
			backgroundColor: color.neutral[1],
			borderTop: `1px solid ${color.neutral[2]}`,
		},
		'& .MuiTableCell-body': {
			...themes.bodyRegular,
			color: color.text.dark,
			padding: '3px 8px !important',
		},
		'& .MuiTableCell-root': {
			borderBottom: `1px solid ${color.neutral[2]}`,
			padding: '13px 8px',
		},
		'& .MuiTableRow-hover': {
			'&:hover': {
				backgroundColor: `${color.signal.infoLight} !important`,
			},
		},
	},
	pagination: {
		'& .MuiToolbar-root.MuiTablePagination-toolbar': {
			position: 'absolute',
			right: 20,
			paddingTop: '5px',
		},
		'& .MuiButtonBase-root.Mui-selected': {
			backgroundColor: color.primary[100],
			color: color.shades.white,
		},
		'& .MuiButtonBase-root.Mui-selected:hover': {
			backgroundColor: color.primary[100],
		},
		'& .MuiPagination-root.MuiPagination-text': {
			flexShrink: 0,
			marginLeft: '10px',
		},
	},
}));
